import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { CalendarDays, Eraser, ImageOff, Images, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import type { TrackerPruneResult, TrackerUser } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { SectionHeader } from '@/components/ui/section-header';
import { useTrackerActivity } from '@/hooks/tracking/useTrackerActivity';
import { useTrackerImages } from '@/hooks/tracking/useTrackerImages';
import { usePruneTrackerImages } from '@/hooks/tracking/usePruneTrackerImages';
import { formatTimeIST, getTodayIST, monthDateRange } from '@/lib/dateFormat';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { TrackerLightbox } from './TrackerLightbox';
import { TrackerPersonPicker } from './TrackerPersonPicker';
import { TrackerShotImage } from './TrackerShotImage';
import { formatCount, rangeLabel } from './trackerConstants';

const PAGE_SIZE = 56;
/** How many years back the calendar's year picker reaches. */
const YEARS_BACK = 6;
/** Heat shade per level, lightest to darkest; a day's level is its share of the month's busiest day. */
const HEAT_CLASSES = [
  '[&>button]:bg-primary/15',
  '[&>button]:bg-primary/30',
  '[&>button]:bg-primary/45',
  '[&>button]:bg-primary/60',
] as const;

interface Props {
  users: TrackerUser[];
  userId: string | null;
  onUserChange: (userId: string | null) => void;
  canManage: boolean;
}

interface Range {
  start: string;
  end: string;
}

const dayKey = (d: Date): string => format(d, 'yyyy-MM-dd');

export function TrackerScreenshots({ users, userId, onUserChange, canManage }: Props) {
  const today = getTodayIST();
  const [month, setMonth] = useState(() => parseISO(today));
  const [range, setRange] = useState<Range>({ start: today, end: today });
  const [page, setPage] = useState(1);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [pruneFound, setPruneFound] = useState<TrackerPruneResult | null>(null);

  const monthRange = monthDateRange(month.getFullYear(), month.getMonth() + 1);
  const activity = useTrackerActivity({ user_id: userId ?? undefined, ...monthRange });

  const single = range.start === range.end;
  const images = useTrackerImages({
    page,
    limit: PAGE_SIZE,
    user_id: userId ?? undefined,
    ...(single ? { date: range.start } : { from: range.start, to: range.end }),
  });
  const prune = usePruneTrackerImages();

  const monthTotal = (activity.data ?? []).reduce((sum, d) => sum + d.captures, 0);

  /** Days bucketed into the 4 heat levels; days inside the selection keep the selection colour. */
  const heat = useMemo(() => {
    const days = activity.data ?? [];
    const max = Math.max(0, ...days.map((d) => d.captures));
    const levels: Date[][] = HEAT_CLASSES.map(() => []);
    for (const d of days) {
      if (d.captures <= 0 || max === 0 || (d.date >= range.start && d.date <= range.end)) continue;
      const level = Math.min(HEAT_CLASSES.length, Math.ceil((d.captures / max) * HEAT_CLASSES.length)) - 1;
      levels[level]?.push(parseISO(d.date));
    }
    return levels;
  }, [activity.data, range]);

  const names = useMemo(() => new Map(users.map((u) => [u.userId, u.name || u.userId])), [users]);
  const ownerName = (id: string) => names.get(id) ?? id;

  const shots = images.data?.data ?? [];
  const pagination = images.data?.pagination;

  const thisYear = parseISO(today).getFullYear();

  const selectRange = (start: string, end: string) => {
    setRange({ start, end });
    setPage(1);
  };

  const goToday = () => {
    setMonth(parseISO(today));
    selectRange(today, today);
  };

  const runDryPrune = () =>
    prune.mutate(
      { user_id: userId ?? undefined, dry_run: true },
      {
        onSuccess: (found) => {
          if (found.missing === 0) {
            toast.success('Everything is present', { description: `Checked ${formatCount(found.checked)} files — none missing.` });
          } else {
            setPruneFound(found);
          }
        },
      },
    );

  const runPrune = () =>
    prune.mutate(
      { user_id: userId ?? undefined, dry_run: false },
      {
        onSuccess: (done) =>
          toast.success('Cleanup done', {
            description: `Removed ${formatCount(done.imagesDeleted)} screenshots and ${formatCount(done.capturesDeleted)} empty captures.`,
          }),
      },
    );

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[300px_1fr]">
      <Card>
        <SectionHeader icon={<CalendarDays />} title="Filter" />
        <CardContent className="space-y-3 p-4">
          <TrackerPersonPicker
            users={users}
            value={userId}
            onChange={(id) => { setPage(1); onUserChange(id); }}
          />

          <Calendar
            mode="range"
            month={month}
            onMonthChange={setMonth}
            startMonth={new Date(thisYear - YEARS_BACK, 0)}
            endMonth={new Date(thisYear + 1, 11)}
            showOutsideDays={false}
            selected={{ from: parseISO(range.start), to: parseISO(range.end) }}
            onSelect={(r, clicked) => {
              // Re-clicking a selected day clears the range; treat that as picking just that day.
              if (!r?.from) return selectRange(dayKey(clicked), dayKey(clicked));
              selectRange(dayKey(r.from), dayKey(r.to ?? r.from));
            }}
            modifiers={Object.fromEntries(heat.map((days, i) => [`heat${i + 1}`, days]))}
            modifiersClassNames={Object.fromEntries(HEAT_CLASSES.map((cls, i) => [`heat${i + 1}`, cls]))}
            className="mx-auto w-fit p-0"
          />

          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              {activity.isLoading ? '—' : `${formatCount(monthTotal)} captures in ${format(month, 'MMMM')}`}
            </span>
            <Button size="sm" variant="outline" onClick={goToday}>Today</Button>
          </div>
          <p className="text-xs text-muted-foreground">Showing {rangeLabel(range.start, range.end)}</p>
          {activity.isError && <p className="text-xs text-destructive">{getErrorMessage(activity.error)}</p>}

          {canManage && (
            <Button variant="outline" size="sm" className="w-full" onClick={runDryPrune} disabled={prune.isPending}>
              <Eraser className="h-3.5 w-3.5" /> Clean up deleted files
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <SectionHeader
          icon={<Images />}
          title={`Screenshots · ${rangeLabel(range.start, range.end)}`}
          hint={`${userId ? ownerName(userId) : 'Everyone'} · ${formatCount(pagination?.total)} total`}
        />
        <CardContent className="p-4">
          {images.isLoading ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }, (_, i) => <div key={i} className="aspect-video animate-pulse rounded-lg bg-muted/40" />)}
            </div>
          ) : images.isError ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center text-destructive">
              <XCircle className="h-8 w-8 opacity-60" />
              <span className="text-sm font-medium">{getErrorMessage(images.error)}</span>
              <Button size="sm" variant="outline" onClick={() => images.refetch()}>Retry</Button>
            </div>
          ) : shots.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center text-muted-foreground">
              <ImageOff className="h-8 w-8 opacity-60" />
              <span className="text-sm">Nothing captured on {rangeLabel(range.start, range.end)}</span>
              <span className="text-xs">Pick another day on the calendar.</span>
            </div>
          ) : (
            <div className={cn('grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4', images.isPlaceholderData && 'opacity-60')}>
              {shots.map((shot, i) => (
                <button
                  key={shot.id}
                  type="button"
                  onClick={() => setLightbox(i)}
                  className="overflow-hidden rounded-lg border border-border/60 text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <TrackerShotImage image={shot} className="aspect-video w-full" />
                  <div className="px-2 py-1.5">
                    <p className="text-xs font-medium">{formatTimeIST(shot.capturedAt)} · {shot.monitor}</p>
                    <p className="truncate text-[11px] text-muted-foreground">{ownerName(shot.userId)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
        {pagination && pagination.total > 0 && (
          <PaginationBar
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            onPageChange={setPage}
          />
        )}
      </Card>

      <TrackerLightbox shots={shots} index={lightbox} ownerName={ownerName} onIndexChange={setLightbox} />

      <ConfirmDialog
        open={!!pruneFound}
        onOpenChange={(open) => { if (!open) setPruneFound(null); }}
        title="Clean up deleted files?"
        description={pruneFound
          ? `${formatCount(pruneFound.missing)} of ${formatCount(pruneFound.checked)} screenshots are no longer in the bucket. Their records will be removed.`
          : undefined}
        confirmLabel="Clean up"
        onConfirm={runPrune}
      />
    </div>
  );
}
